import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router';

import { ChatPanel } from './chat/ChatPanel';
import { api, AUTH_REQUIRED_EVENT, getToken, setToken } from './lib/api';
import { Labs } from './pages/Labs';
import { Overview } from './pages/Overview';
import { Bookings } from './pages/Bookings';
import { Pages } from './pages/Pages';
import { Settings } from './pages/Settings';
import { Transcripts } from './pages/Transcripts';
import { Documents } from './pages/Documents';
import { Apartments } from './pages/Apartments';

const ROLE_LABEL = { owner: 'владелец', assistant: 'ассистент', viewer: 'просмотр' } as const;

function TokenDialog({ onSaved }: { onSaved: () => void }) {
  const [value, setValue] = useState(getToken() ?? '');
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setToken(value.trim() || undefined);
    onSaved();
  };
  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-labelledby="token-title">
      <form className="card dialog" onSubmit={submit}>
        <h2 id="token-title">Нужен токен доступа</h2>
        <p className="muted">
          Сервер защищён. Введите значение <code>WEB_AUTH_TOKEN</code> из <code>.env</code>. Токен
          сохранится только в этом браузере.
        </p>
        <input
          type="password"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          autoFocus
          autoComplete="current-password"
          placeholder="Токен"
        />
        <button type="submit" className="btn btn--primary" disabled={!value.trim()}>
          Войти
        </button>
      </form>
    </div>
  );
}

/** Old addresses (bookmarks, WHOOP answers in flight) lead to their new place. */
function MovedTo({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

export function App() {
  const queryClient = useQueryClient();
  const [needsToken, setNeedsToken] = useState(false);
  const status = useQuery({ queryKey: ['status'], queryFn: api.status, refetchInterval: 30_000 });
  const me = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 60_000 });
  const session = me.data?.mode === 'session';
  const chat = me.data?.chat ?? false;
  const isOwner = me.data?.user.role === 'owner';
  const writes = (area: 'pages' | 'calendar') => me.data?.permissions[area] === 'write';

  useEffect(() => {
    // Signed in on the site: an expired session goes back to the sign-in page. Locally: token.
    const onAuthRequired = () => {
      if (!['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname)) {
        window.location.assign('/login');
      } else {
        setNeedsToken(true);
      }
    };
    window.addEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, onAuthRequired);
  }, []);

  const onTokenSaved = () => {
    setNeedsToken(false);
    void queryClient.invalidateQueries();
  };

  return (
    <div className="shell">
      <header className="topbar">
        <div className="brand">
          <img src="/logo.png" alt="" width={30} height={30} />
          <span>CyberNika</span>
        </div>
        <nav className="nav">
          <NavLink to="/" end>
            Обзор
          </NavLink>
          <NavLink to="/labs">Анализы</NavLink>
          <NavLink to="/pages">Страницы</NavLink>
          <NavLink to="/bookings">Записи</NavLink>
          <NavLink to="/transcripts">Расшифровки</NavLink>
          <NavLink to="/documents">Документы</NavLink>
          <NavLink to="/apartments">Квартиры</NavLink>
          {chat && <NavLink to="/chat">Чат</NavLink>}
          <NavLink to="/settings">Настройки</NavLink>
        </nav>
        {session && me.data && (
          <div className="userbox">
            <span className="muted small">
              {me.data.user.displayName ?? me.data.user.name} · {ROLE_LABEL[me.data.user.role]}
            </span>
            <form method="post" action="/logout">
              <button type="submit" className="btn btn--small btn--ghost">
                Выйти
              </button>
            </form>
          </div>
        )}
      </header>

      <main className="main">
        {status.isPending && <div className="page muted">Загрузка…</div>}
        {status.isError && (
          <div className="page">
            <div className="card">
              <h3>Сервер недоступен</h3>
              <p className="text-error">{status.error.message}</p>
              <button type="button" className="btn" onClick={() => void status.refetch()}>
                Повторить
              </button>
            </div>
          </div>
        )}
        {status.data && (
          <Routes>
            <Route path="/" element={<Overview status={status.data} />} />
            <Route path="/labs" element={<Labs />} />
            <Route path="/pages" element={<Pages canDelete={writes('pages')} />} />
            <Route path="/bookings" element={<Bookings canWrite={writes('calendar')} />} />
            <Route path="/transcripts" element={<Transcripts />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/apartments" element={<Apartments />} />
            {chat && (
              <Route
                path="/chat"
                element={
                  <div className="chat-page">
                    <ChatPanel status={status.data.agent} />
                  </div>
                }
              />
            )}
            <Route
              path="/settings/*"
              element={<Settings signedIn={session} isOwner={isOwner} status={status.data} />}
            />
            <Route path="/audit" element={<MovedTo to="/settings/journal" />} />
            <Route path="/connections" element={<MovedTo to="/settings/connections" />} />
            <Route path="*" element={<div className="page muted">Страница не найдена.</div>} />
          </Routes>
        )}
      </main>

      {needsToken && <TokenDialog onSaved={onTokenSaved} />}
    </div>
  );
}
